import React, {useState, useEffect, useRef, useCallback} from 'react';
import {TextArea} from '@blueprintjs/core';

import {validate} from '../validate';
import {StyledFormGroup, RoundedInputWrap} from './shared';


const parseJsonError = (text) => {
    if (!text || text.trim() === '') return null;
    try {
        JSON.parse(text);
        return null;
    } catch (e) {
        const match = e.message.match(/position (\d+)/);
        if (match) {
            const pos = parseInt(match[1], 10);
            const before = text.slice(0, pos);
            const line = (before.match(/\n/g) || []).length + 1;
            const col = pos - before.lastIndexOf('\n');
            return {message: `Invalid JSON at line ${line}, col ${col}`};
        }
        return {message: e.message};
    }
};

const serialize = (value) => {
    if (value === undefined || value === null) return '';
    return JSON.stringify(value, null, 2);
};

const JsonTextAreaField = ({label, value, disabled, error, rows = 10, schema, onChange}) => {
    const [text, setText] = useState(() => serialize(value));
    const [localError, setLocalError] = useState(null);
    const timerRef = useRef(null);
    const lastExternalRef = useRef(value);

    // Sync from external value changes (not from our own onChange)
    useEffect(() => {
        if (value !== lastExternalRef.current) {
            lastExternalRef.current = value;
            setText(serialize(value));
            setLocalError(null);
        }
    }, [value]);

    const handleChange = useCallback((e) => {
        const newText = e.target.value;
        setText(newText);

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            // Check JSON syntax
            const jsonErr = parseJsonError(newText);
            if (jsonErr) {
                setLocalError(jsonErr);
                return;
            }

            // Empty text → undefined
            if (!newText || newText.trim() === '') {
                setLocalError(null);
                lastExternalRef.current = undefined;
                onChange(undefined);
                return;
            }

            // Parse succeeded — validate against schema if provided
            const parsed = JSON.parse(newText);
            let schemaErr = null;
            if (schema?.properties) {
                const schemaErrors = validate(schema, parsed);
                if (schemaErrors.length > 0) {
                    schemaErr = {message: schemaErrors.map(e => `${e.instancePath || '/'}: ${e.message}`).join('; ')};
                }
            }

            setLocalError(schemaErr);
            lastExternalRef.current = parsed;
            onChange(parsed);
        }, 500);
    }, [schema, onChange]);

    // Cleanup timer
    useEffect(() => () => clearTimeout(timerRef.current), []);

    const displayError = error || localError;
    const intent = displayError ? 'danger' : 'none';

    return (
        <StyledFormGroup label={label} helperText={displayError?.message} intent={intent}>
            <RoundedInputWrap>
                <TextArea
                    fill={true}
                    rows={rows}
                    value={text}
                    disabled={disabled}
                    intent={intent}
                    onChange={handleChange}
                    style={{fontFamily: 'monospace', resize: 'vertical', minHeight: `${rows * 1.4}em`}}
                />
            </RoundedInputWrap>
        </StyledFormGroup>
    );
};

export {JsonTextAreaField};
